import { WEBSOCKET_CONFIG } from '../config/constants.js'
import { logger } from '../lib/logger.js'
import { getLiveGatewaySendPriority } from '../lib/live-message-priority.js'

export interface GatewayConnectionOptions {
  url?: string
  timeoutMs?: number
}

export interface GatewayConnection {
  readonly url: string
  readonly socket: WebSocket
  isOpen(): boolean
  send(message: unknown): boolean
  close(code?: number, reason?: string): void
  onMessage(cb: (message: MessageEvent) => void): () => void
  onClose(cb: (event: CloseEvent) => void): () => void
  onError(cb: (event: Event) => void): () => void
}

export function createGatewayConnection(
  options: GatewayConnectionOptions = {}
): Promise<GatewayConnection> {
  const url = options.url || getDefaultGatewayUrl()
  const timeoutMs = options.timeoutMs ?? WEBSOCKET_CONFIG.HEARTBEAT_TIMEOUT_MS

  return new Promise((resolve, reject) => {
    let settled = false
    const socket = new WebSocket(url)

    const timeoutId = setTimeout(() => {
      if (settled) return
      settled = true
      cleanupOpenListeners()
      try {
        socket.close()
      } catch {
        /* ignore close errors */
      }
      reject(new Error(`Live gateway connection timed out after ${timeoutMs}ms`))
    }, timeoutMs)

    const cleanupOpenListeners = () => {
      clearTimeout(timeoutId)
      socket.removeEventListener('open', handleOpen)
      socket.removeEventListener('error', handleOpenError)
      socket.removeEventListener('close', handleEarlyClose)
    }

    const handleOpen = () => {
      if (settled) return
      settled = true
      cleanupOpenListeners()
      logger.debug('[LiveAuth] Gateway WebSocket opened', { url })
      resolve(createConnection(socket, url))
    }

    const handleOpenError = () => {
      if (settled) return
      settled = true
      cleanupOpenListeners()
      reject(new Error('Live gateway WebSocket failed to open'))
    }

    const handleEarlyClose = (event: CloseEvent) => {
      if (settled) return
      settled = true
      cleanupOpenListeners()
      reject(
        new Error(
          `Live gateway WebSocket closed before opening${
            event.reason ? `: ${event.reason} (code: ${event.code})` : ` (code: ${event.code})`
          }`
        )
      )
    }

    socket.addEventListener('open', handleOpen)
    socket.addEventListener('error', handleOpenError)
    socket.addEventListener('close', handleEarlyClose)
  })
}

function getDefaultGatewayUrl(): string {
  return appendWebSocketPath(WEBSOCKET_CONFIG.URL, WEBSOCKET_CONFIG.LIVE_GATEWAY_PATH)
}

function appendWebSocketPath(baseUrl: string, path: string): string {
  try {
    const url = new URL(baseUrl)
    url.pathname = path
    return url.toString().replace(/\/$/, '')
  } catch (error) {
    logger.warn('[LiveAuth] Failed to build Live gateway URL; using raw WebSocket URL', {
      baseUrl,
      path,
      error: error instanceof Error ? error.message : String(error),
    })
    return baseUrl
  }
}

function createConnection(socket: WebSocket, url: string): GatewayConnection {
  return {
    url,
    socket,
    isOpen() {
      return socket.readyState === WebSocket.OPEN
    },
    send(message: unknown) {
      if (socket.readyState !== WebSocket.OPEN) {
        logger.warn('[LiveAuth] Dropping message because socket is not open', {
          readyState: socket.readyState,
        })
        return false
      }

      const priority = getLiveGatewaySendPriority(message)
      const bufferedAmount = socket.bufferedAmount

      // Drop video frames first when backpressure builds — protects audio/text/tool traffic.
      if (
        priority === 'droppable' &&
        bufferedAmount > WEBSOCKET_CONFIG.HIGH_BUFFER_THRESHOLD
      ) {
        logger.debug('[LiveAuth] Dropping video frame due to backpressure', {
          bufferedAmount,
          threshold: WEBSOCKET_CONFIG.HIGH_BUFFER_THRESHOLD,
        })
        return false
      }

      if (bufferedAmount > WEBSOCKET_CONFIG.MAX_BUFFERED_AMOUNT && priority !== 'critical') {
        logger.warn('[LiveAuth] Dropping non-critical message because socket buffer is full', {
          bufferedAmount,
          threshold: WEBSOCKET_CONFIG.MAX_BUFFERED_AMOUNT,
          priority,
        })
        return false
      }

      if (bufferedAmount > WEBSOCKET_CONFIG.MAX_BUFFERED_AMOUNT && priority === 'critical') {
        logger.warn('[LiveAuth] Sending critical message despite full buffer', {
          bufferedAmount,
          threshold: WEBSOCKET_CONFIG.MAX_BUFFERED_AMOUNT,
        })
      }

      socket.send(JSON.stringify(message))
      return true
    },
    close(code?: number, reason?: string) {
      if (socket.readyState === WebSocket.CLOSING || socket.readyState === WebSocket.CLOSED) {
        return
      }
      socket.close(code, reason)
    },
    onMessage(cb: (message: MessageEvent) => void) {
      socket.addEventListener('message', cb)
      return () => socket.removeEventListener('message', cb)
    },
    onClose(cb: (event: CloseEvent) => void) {
      socket.addEventListener('close', cb)
      return () => socket.removeEventListener('close', cb)
    },
    onError(cb: (event: Event) => void) {
      socket.addEventListener('error', cb)
      return () => socket.removeEventListener('error', cb)
    },
  }
}
